import { formatDate } from './formatters';

/**
 * Terminología del dashboard:
 * - Código de licencia: contrato (License).
 * - Licencias contratadas: suma de max_installations (lo que se cobra).
 * - Instalaciones realizadas: equipos registrados.
 * - Cupos libres: cupos - instaladas.
 */

export const USAGE_STATUS = {
  EMPTY: 'empty',
  NORMAL: 'normal',
  FULL: 'full',
  EXCEEDED: 'exceeded',
};

export const USAGE_STATUS_LABELS = {
  [USAGE_STATUS.EMPTY]: 'Sin instalar',
  [USAGE_STATUS.NORMAL]: '',
  [USAGE_STATUS.FULL]: 'Completo',
  [USAGE_STATUS.EXCEEDED]: 'Excedido',
};

export const USAGE_STATUS_BADGE = {
  [USAGE_STATUS.EMPTY]: 'secondary',
  [USAGE_STATUS.NORMAL]: 'info',
  [USAGE_STATUS.FULL]: 'success',
  [USAGE_STATUS.EXCEEDED]: 'danger',
};

export const getUsageStatus = (seats, installed) => {
  if (installed > seats) return USAGE_STATUS.EXCEEDED;
  if (installed === 0) return USAGE_STATUS.EMPTY;
  if (installed === seats) return USAGE_STATUS.FULL;
  return USAGE_STATUS.NORMAL;
};

/** Porcentaje entero; puede superar 100 si hay más instalaciones que cupos. */
export const getUsagePercent = (seats, installed) => {
  if (!seats) return installed > 0 ? 100 : 0;
  return Math.round((installed / seats) * 100);
};

export const USAGE_SORT_OPTIONS = [
  { value: 'free_desc', label: 'Más cupos libres' },
  { value: 'usage_desc', label: 'Mayor % de uso' },
  { value: 'usage_asc', label: 'Menor % de uso' },
  { value: 'seats_desc', label: 'Más licencias contratadas' },
  { value: 'name_asc', label: 'Nombre (A-Z)' },
];

export const sortUsageClients = (clients, sortBy) => {
  const list = [...clients];
  const pct = (c) => getUsagePercent(c.seats, c.installed);
  const byName = (a, b) => a.client_name.localeCompare(b.client_name, 'es');

  switch (sortBy) {
    case 'usage_desc':
      return list.sort((a, b) => pct(b) - pct(a) || byName(a, b));
    case 'usage_asc':
      return list.sort((a, b) => pct(a) - pct(b) || byName(a, b));
    case 'seats_desc':
      return list.sort((a, b) => b.seats - a.seats || byName(a, b));
    case 'name_asc':
      return list.sort(byName);
    case 'free_desc':
    default:
      return list.sort((a, b) => b.free - a.free || byName(a, b));
  }
};

const normalizeText = (text) =>
  (text || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export const filterUsageClients = (clients, search) => {
  const term = normalizeText(search).trim();
  if (!term) return clients;
  return clients.filter(
    (c) =>
      normalizeText(c.client_name).includes(term) ||
      normalizeText(c.company).includes(term)
  );
};

export const formatSeatsUsage = (seats, installed) => `${installed} de ${seats}`;

export const formatLicenseExpiry = (code) => {
  if (code.perpetual_validity) return 'Indefinida';
  if (!code.expiry_date) return 'Sin vencimiento';
  return formatDate(code.expiry_date, 'short');
};

export const sumUsage = (clients) =>
  clients.reduce(
    (acc, c) => {
      acc.clients += 1;
      acc.license_codes += c.license_codes;
      acc.seats += c.seats;
      acc.installed += c.installed;
      acc.free += c.free;
      return acc;
    },
    { clients: 0, license_codes: 0, seats: 0, installed: 0, free: 0 }
  );
